import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Platform,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { duration, easing } from '@/theme/tokens.generated';

// The floating glass dock (components/tab-bar.tsx) is absolutely positioned so
// content can scroll beneath it. Two things follow from that, both owned here:
//  1. Every screen must know the dock's height: a screen that does NOT opt in
//     keeps the dock's footprint reserved (ScreenContainer), and a screen that
//     scrolls under it pads its own list by the same amount (useDockInset).
//  2. The dock can slide away while the reader scrolls down and return on
//     scroll up, at the top of a list, on navigation, or on keyboard focus.

// Height of the dock including its safe-area padding; provided by the (app)
// tab layout. 0 outside the tabs (unauthenticated screens have no dock).
export const DockInsetContext = createContext(0);
export const useDockInset = () => useContext(DockInsetContext);

// Bottom padding a scroll container needs so its last item clears whatever sits
// at the screen edge: the dock when there is one, else the home-indicator inset.
export function dockClearance(dockInset: number, safeBottom: number): number {
  return dockInset > 0 ? dockInset : safeBottom;
}
export function useDockClearance(): number {
  const dockInset = useDockInset();
  const insets = useSafeAreaInsets();
  return dockClearance(dockInset, insets.bottom);
}

const HIDE_AFTER_OFFSET = 80; // never hide while still near the top
const MIN_SCROLL_DELTA = 8; // ignore jitter and momentum wobble

// Pure rule: scrolling down past the threshold hides the dock, scrolling up
// (or returning near the top) shows it, anything small keeps the current state.
export function nextDockHidden(hidden: boolean, offset: number, lastOffset: number): boolean {
  if (offset <= HIDE_AFTER_OFFSET) return false;

  const delta = offset - lastOffset;
  if (delta > MIN_SCROLL_DELTA) return true;
  if (delta < -MIN_SCROLL_DELTA) return false;

  return hidden;
}

// Consumed by the tab bar's style (React Navigation's tabBarStyle accepts
// animated values), so the slide runs on the native driver.
export const dockTranslateY = new Animated.Value(0);

let hidden = false;
let hideDistance = 140;
let reduceMotion = false;

if (Platform.OS !== 'web' || typeof window !== 'undefined') {
  AccessibilityInfo.isReduceMotionEnabled?.().then(
    (value) => (reduceMotion = value),
    () => {},
  );
  AccessibilityInfo.addEventListener?.('reduceMotionChanged', (value) => (reduceMotion = value));
}

export function setDockHideDistance(distance: number) {
  hideDistance = distance;
}

export function setDockHidden(next: boolean) {
  if (next === hidden) return;
  hidden = next;

  Animated.timing(dockTranslateY, {
    toValue: next ? hideDistance : 0,
    duration: reduceMotion ? 0 : duration.base,
    easing: Easing.bezier(...easing.easeOut),
    useNativeDriver: true,
  }).start();
}

// Keyboard users tabbing into a hidden dock must see where focus is.
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  document.addEventListener('focusin', (event) => {
    const target = event.target as Element | null;
    if (hidden && target?.closest?.('[role="tablist"], [role="tab"]')) setDockHidden(false);
  });
}

// Wire a scrolling list (FlatList / ScrollView) to the dock: spread the result
// onto it. Resets the dock when the screen mounts or unmounts, so navigating
// never leaves it hidden.
export function useDockScroll() {
  const lastOffset = useRef(0);

  useEffect(() => {
    setDockHidden(false);
    return () => setDockHidden(false);
  }, []);

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offset = event.nativeEvent.contentOffset.y;
    setDockHidden(nextDockHidden(hidden, offset, lastOffset.current));
    lastOffset.current = offset;
  }, []);

  return { onScroll, scrollEventThrottle: 16 };
}
